import React, { useState } from "react";
import Alert from "@material-ui/core/Alert";
import LoadingButton from "@material-ui/lab/LoadingButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Box from "@material-ui/core/Box";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import {
  useForgotPasswordMutation,
  useSolicitarResetPortalMutation,
} from "src/services/loginApi";

import { validateRut, deconstructRut } from "@fdograph/rut-utilities";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";

export default function ForgotPassword({ open, onClose, title, isClient }) {
  const [error, setError] = useState("");
  const [forgotPassword] = useForgotPasswordMutation();
  const [solicitarResetPortal] = useSolicitarResetPortalMutation();

  const rutValidation = Yup.string()
    .required("RUT requerido")
    .test("rut test", "RUT no válido (ej: 12.345.678-9)", (value) =>
      validateRut(value),
    );

  const LoginSchema = isClient
    ? Yup.object().shape({ rut: rutValidation })
    : Yup.object().shape({
        rut: rutValidation,
        correo: Yup.string()
          .email("Correo no válido")
          .required("Correo requerido"),
      });

  const formik = useFormik({
    initialValues: { rut: "", correo: "" },
    validationSchema: LoginSchema,
    onSubmit: () => {
      guardarDatos();
    },
  });

  const {
    errors,
    touched,
    isSubmitting,
    setSubmitting,
    handleSubmit,
    getFieldProps,
  } = formik;

  const getRutLimpio = () => {
    const raw = getFieldProps("rut").value.toUpperCase();
    return deconstructRut(raw).digits + deconstructRut(raw).verifier;
  };

  const guardarDatos = async () => {
    setSubmitting(true);
    setError("");

    try {
      if (isClient) {
        const res = await solicitarResetPortal({
          rut: getRutLimpio(),
        }).unwrap();
        setSubmitting(false);
        onClose(res.mensaje);
      } else {
        const res = await forgotPassword({
          rut: getRutLimpio(),
          correo: getFieldProps("correo").value,
        }).unwrap();
        setSubmitting(false);
        onClose(res.mensaje);
      }
    } catch {
      setSubmitting(false);
      setError("Ha ocurrido un error. Verifica que los datos sean correctos.");
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="xs">
      <DialogTitle>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <LockOutlinedIcon color="primary" />
          {title || "Recuperar Contraseña"}
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isClient
            ? "Ingresa tu RUT. Si está registrado en el sistema, recibirás un correo con el enlace para restablecer tu contraseña."
            : "Ingresa el RUT y correo electrónico asociados a tu cuenta. Te enviaremos las instrucciones para restablecer tu contraseña."}
        </DialogContentText>

        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <TextField
              required
              margin="dense"
              id="rut"
              label="RUT"
              fullWidth
              {...getFieldProps("rut")}
              placeholder={isClient ? "Ej: 12.345.678-9" : "Ingresa tu RUT"}
              error={Boolean(touched.rut && errors.rut)}
              helperText={touched.rut && errors.rut}
            />

            {!isClient && (
              <TextField
                required
                margin="dense"
                id="correo"
                label="Correo electrónico"
                type="email"
                fullWidth
                {...getFieldProps("correo")}
                error={Boolean(touched.correo && errors.correo)}
                helperText={touched.correo && errors.correo}
              />
            )}

            {error && (
              <Alert severity="error" style={{ marginTop: 12 }}>
                {error}
              </Alert>
            )}

            <DialogActions
              style={{ marginTop: 16, paddingLeft: 0, paddingRight: 0 }}
            >
              <LoadingButton
                onClick={() => onClose("")}
                color="error"
                disabled={isSubmitting}
              >
                Volver
              </LoadingButton>

              <LoadingButton
                type="submit"
                color="primary"
                variant="contained"
                loading={isSubmitting}
              >
                Enviar instrucciones
              </LoadingButton>
            </DialogActions>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
